"use client";

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeftIcon, PencilSquareIcon } from "@heroicons/react/24/solid";
import { db, functions } from "../../../config/firebaseConfig";
import {
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { Button } from "../../../components/catalyst/button";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { Dialog } from "@headlessui/react";
import { httpsCallable } from "firebase/functions";
import { useToast } from "../../../util/ToastProvider";

export default function AnnexureView() {
  const navigate = useNavigate();
  const { id } = useParams();
  const templateManagerHandler = httpsCallable(functions, "templateManager");
  const toaster = useToast();
  
  
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useSelector((s: RootState) => s.auth);

  const [templates, setTemplates] = useState<any[]>([]);
  const [usedByTemplates, setUsedByTemplates] = useState<any[]>([]);

  // dialog states
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [usedOpen, setUsedOpen] = useState(false);

  const [deleting, setDeleting] = useState(false);

  async function handleDelete() {
    try {
      setDeleting(true);

      const res = await templateManagerHandler({
        action: "deleteLayerOrAnnexure",
        type: "annexure",
        id: data.id
      });

      console.log("Deleted:", res);

      // Close dialog
      setDeleteOpen(false);
      toaster.success("Annexures deleted successfully");
      // Navigate back to list
      navigate("/admin/annexures");
    } catch (err:any) {
      console.error(err);
      toaster.error(err.message || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  }

  // ----------------------------------------
  // LOAD ANNEXURE DATA
  // ----------------------------------------
  useEffect(() => {
    if (!id) return;

    async function load() {
      if (!id) return;
      const ref = doc(db, "annexures", "meta", "data", id);
      const snap = await getDoc(ref);

      if (snap.exists()) {
        const d = snap.data();
        setData({ id, ...d });
        loadTemplates(id);
      }

      setLoading(false);
    }

    load();
  }, [id]);

  // ----------------------------------------
  // CHECK WHICH TEMPLATES USE THIS ANNEXURE
  // ----------------------------------------
  useEffect(() => {
    if (!id) return;
  
    let mounted = true;
  
    (async () => {
      try {
        const snap = await getDocs(collectionGroup(db, "versions"));
  
        const results: any[] = [];
  
        snap.forEach((docSnap) => {
          const data = docSnap.data();
  
          // CASE 1: array of ids
          const arr1 = data.annexureIds || [];
  
          // CASE 2: array of objects [{ id, title }]
          const arr2 = (data.annexures || []).map((a: any) => a.id);
  
          const allIds = [...arr1, ...arr2];
  
          if (allIds.includes(id)) {
            const templateRef = docSnap.ref.parent?.parent;
  
            results.push({
              templateId: templateRef?.id,
              versionId: docSnap.id,
              title: data.title ?? null,
              updatedAt: data.updatedAt ?? null,
            });
          }
        });
  
        if (mounted) setUsedByTemplates(results);
      } catch (err) {
        console.error("Error fetching templates for annexure:", err);
      }
    })();
  
    return () => {
      mounted = false;
    };
  }, [id]);
  

  // ----------------------------------------
  // LOAD ONLY PUBLISHED TEMPLATES FOR DISPLAY TAGS
  // ----------------------------------------
  async function loadTemplates(annexureId: string) {
    const snap = await getDocs(collectionGroup(db, "versions"));
    const results: any[] = [];

    snap.forEach((docSnap) => {
      if (docSnap.id !== "published") return;

      const data = docSnap.data();

      if (data.annexureIds?.includes(annexureId)) {
        const parent = docSnap.ref.parent?.parent;
        if (!parent) return;

        results.push({
          templateId: parent.id,
          ...data,
        });
      }
    });

    setTemplates(results);
  }

  // ----------------------------------------
  // RENDER
  // ----------------------------------------

  if (loading) return <div className="p-8 text-white">Loading...</div>;
  if (!data) return <div className="p-8 text-white">Annexure not found</div>;

  const isUsed = usedByTemplates.some((t) => t.versionId === "published");

  return (
    <div className="p-8 space-y-10">
      {/* Back */}
      <button
        type="button"
        onClick={() => navigate("/admin/annexures")}
        className="inline-flex items-center gap-2 text-sm text-gray-500"
      >
        <ChevronLeftIcon className="h-4 w-4" />
        Annexures
      </button>

      {/* Title + Delete */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">
          {data.number}: {data.title}
        </h1>

        <button
          onClick={() => {
            if (isUsed) setUsedOpen(true);
            else setDeleteOpen(true);
          }}
          className="px-4 py-2 border border-[2px] !border-dark-border-danger !text-dark-border-danger rounded-lg"
        >
          Delete
        </button>
      </div>

      {/* Connected templates */}
      <div className="mt-8 flex justify-between items-center">
        <div>
          {templates.length > 0 && (
            <div className="space-y-2">
              <h2 className="text-sm font-semibold text-gray-500">
                Connected report template
              </h2>

              <div className="flex flex-wrap gap-2">
                {templates.map((tpl) => (
                  <span
                    key={tpl.templateId}
                    className="px-3 py-1 text-xs rounded-full bg-gray-200 text-gray-700 truncate max-w-[20ch]"
                  >
                    {tpl.title || tpl.templateId}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {(user?.role === "admin" || user?.role === "approver") && (
          <Button
            color="brand"
            onClick={() => navigate(`/admin/annexures/${data.id}/edit`)}
            className="flex items-center gap-2"
          >
            <PencilSquareIcon className="h-4 w-4 text-white" />
            Edit
          </Button>
        )}
      </div>

      {/* Body */}
      <div className="border p-6 rounded-xl">
        <div className="prose max-w-none">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {(data.instructions || data.body || "").replace(/\\n/g, "\n")}
          </ReactMarkdown>
        </div>
      </div>

      {/* ----------------------------------------------------------------- */}
      {/* Dialog: Annexure is used */}
      {/* ----------------------------------------------------------------- */}
      <Dialog open={usedOpen} onClose={() => {}} className="relative z-50">
        <div className="fixed inset-0 bg-black/40" />

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <Dialog.Title className="text-lg font-semibold text-zinc-800">
              Annexure is used by a template
            </Dialog.Title>

            <p className="mt-3 text-sm text-zinc-600">
              This annexure is used by one or more templates, therefore it cannot be deleted.
              Remove this annexure from those templates first, then delete it.
            </p>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setUsedOpen(false)}
                className="rounded-md bg-brand-600 px-4 py-2 text-sm text-white hover:bg-brand-600/90"
              >
                Close
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* ----------------------------------------------------------------- */}
      {/* Dialog: Delete Confirmation */}
      {/* ----------------------------------------------------------------- */}
      <Dialog open={deleteOpen} onClose={() => {}}  className="relative z-50">
        <div className="fixed inset-0 bg-black/40" />

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <Dialog.Title className="text-lg font-semibold text-zinc-800">
              Delete Annexure?
            </Dialog.Title>

            <p className="mt-3 text-sm text-zinc-600">
              Are you certain you want to delete this annexure? This action cannot be reversed.
            </p>

            <div className="mt-6 flex justify-end gap-3">
              <button
                
                onClick={() => setDeleteOpen(false)}
                className="rounded-md bg-zinc-200 px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-300"
              >
                No, go back
              </button>

              <button
                disabled={deleting}
                onClick={() => {
                  console.log("Delete annexure:", data.id);
                  // TODO: actual deletion logic here
                  handleDelete()
                }}
                className="rounded-md bg-brand-600 px-4 py-2 text-sm text-white hover:bg-brand-700"
              >
                {!deleting ? "Yes, delete" : "Deleteing"}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
}
